import { useState } from 'react';
import { ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'use-intl';
import useAuthDrawer from '@/components/organisms/authentication/auth-drawer-provider/hooks/useAuthDrawer';
import { SIGN_IN_DRAWER_ID } from '@/components/organisms/authentication/sign-in-drawer';
import QuantityWidget from '@/components/atoms/quantity-widget';
import { classnames } from '@/utils/classnames/classnames';
import { resolveAddToCartAvailability } from '@/utils/product/add-to-cart-availability';
import { CartCTAProps } from '../types';

interface ExtendedCartCTAProps extends CartCTAProps {
  // EWM-197 / EWM-653 — Master role only. View-only now collapses the section
  // entirely (no stepper, no Add, and — since purchase lists are Master-only —
  // no list button either): `addToListButton` is undefined for View-only, so
  // this branch renders null. Guest swaps Add for a login link.
  isViewOnly?: boolean;
  isGuest?: boolean;
  // Slot for the Add to Purchase List icon button (rendered to the right of
  // Add). Guest path replaces the entire row with a login link, so the slot
  // is ignored there.
  addToListButton?: React.ReactNode;
}

const CartCTA = ({
  product,
  addToCart,
  countChange,
  addToCartDisabled,
  backOrdersAllowed = false,
  isViewOnly = false,
  isGuest = false,
  addToListButton,
}: ExtendedCartCTAProps) => {
  const translate = useTranslations();
  const { mode: authMode, open: openAuthDrawer } = useAuthDrawer();

  // EWM-278 — business decision (2026-05-29): the PDP quantity
  // stepper no longer enforces min/max bounds. Initial count defaults
  // to 1 (the typical product floor); the underlying cart action
  // validates the final value at submit time.
  const [count, setCount] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const handleCountChange = (next: number) => {
    countChange(next);
    setCount(next);
  };

  // EWM-197 — Guest path: only the login CTA + (optionally) the AddToList
  // icon button render. Quantity stepper is hidden because the user has no
  // signed-in cart context yet.
  if (isGuest) {
    return (
      <div className="flex items-stretch gap-2">
        {/* Sign-in opens the app-wide AuthDrawer, not a `/login` route — that
            path isn't a published page and 404s (EWM-385). */}
        <button
          type="button"
          onClick={() => openAuthDrawer('sign-in')}
          aria-haspopup="dialog"
          aria-controls={SIGN_IN_DRAWER_ID}
          aria-expanded={authMode === 'sign-in'}
          className="bg-primary hover:bg-primary-hover text-16 flex grow items-center justify-center gap-2 rounded-lg py-3 font-bold text-white transition-colors"
        >
          <ShoppingCartIcon className="size-5" aria-hidden />
          {translate('pdp.login-to-add-to-cart')}
        </button>
        {addToListButton}
      </div>
    );
  }

  // EWM-197 / EWM-653 — View-only role hides quantity + Add. Purchase lists
  // are now Master-only so `addToListButton` is undefined for View-only users
  // (gated upstream in MainInfo). Renders null — no action area at all.
  if (isViewOnly) {
    return addToListButton ? <div className="flex items-stretch gap-2">{addToListButton}</div> : null;
  }

  const outOfStock = !product.inStock;
  // EWM-360 — out-of-stock products become an enabled "Back Order" CTA when
  // the customer's BU allows back-orders; GO ("removed from sale") products
  // stay disabled regardless. `addToCartDisabled` still carries the mapper's
  // permission / price / no-stock-signal gate, which back-order can't override.
  const { disabled: stockGateDisabled, isBackOrder } = resolveAddToCartAvailability({
    inStock: product.inStock,
    removedFromSale: product.isRemovedFromSale,
    backOrdersAllowed,
  });
  // maxQuantity is 0 for out-of-stock products — skip that gate for back-order.
  const addDisabled =
    addToCartDisabled || isLoading || stockGateDisabled || (!isBackOrder && (product.maxQuantity ?? 0) === 0);

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-14 mb-3 font-bold text-gray-800">{translate('pdp.quantity-label')}</p>
        {/* EWM-610 — unified onto the shared quantity-widget.
            EWM-616 — `pdp` variant: square buttons + fixed-width input so the
            selector stays compact (was stretching to ~50% of the column).
            EWM-278 — no stock-based cap; only the widget's MAX_QUANTITY ceiling
            applies (EWM-641: restricted customers are gated at cart/checkout, not
            by clamping the stepper — that would leak warehouse stock levels). */}
        <QuantityWidget
          value={count}
          onChange={handleCountChange}
          minValue={1}
          variant="pdp"
          showLabel={false}
          spinbutton
          disabled={outOfStock && !isBackOrder}
        />
      </div>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          disabled={addDisabled}
          // EWM-440 — visible label shortens to "Add" on mobile/tablet, but the
          // accessible name stays the full action so screen readers aren't left
          // with a bare "Add". Only the add state relabels; OOS / back-order
          // keep their own single label.
          aria-label={isBackOrder || outOfStock ? undefined : translate('cart.add')}
          onClick={async () => {
            setIsLoading(true);
            try {
              await addToCart(count);
              setCount(1);
            } finally {
              setIsLoading(false);
            }
          }}
          className={classnames(
            'text-16 flex grow items-center justify-center gap-2 rounded-lg py-3 font-bold transition-colors',
            addDisabled ? 'cursor-not-allowed bg-gray-400 text-white' : 'bg-primary hover:bg-primary-hover text-white',
          )}
        >
          <ShoppingCartIcon className="size-5" aria-hidden />
          {isBackOrder ? (
            translate('pdp.back-order')
          ) : outOfStock ? (
            translate('pdp.out-of-stock-action')
          ) : (
            <>
              {/* EWM-440 — "Add" < lg, full "Add to Cart" from lg up */}
              <span className="lg:hidden">{translate('pdp.add')}</span>
              <span className="hidden lg:inline">{translate('cart.add')}</span>
            </>
          )}
        </button>
        {addToListButton}
      </div>
    </div>
  );
};

export default CartCTA;
